import { ChangeEvent, FocusEvent, forwardRef, InputHTMLAttributes, KeyboardEvent, useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import { UiContainer } from '../../../state/ui';
import { AutocompleteItem } from './SearchSuggestion';

export type SuggestionProps<T> = {
	item: T;
};

export type AutoCompletingInputProps<T> = {
	onPickSuggestion: (item: T) => void;
	suggestions: T[];
	Suggestion: (props: SuggestionProps<T>) => JSX.Element;
} & InputHTMLAttributes<HTMLInputElement>;

export const AutoCompletingInput = forwardRef<HTMLInputElement, AutoCompletingInputProps<any>>(
	({ value, onFocus, onChange, onKeyDown, suggestions, Suggestion, onPickSuggestion, ...props }, ref) => {
		const { isSearchFormFocused, setIsSearchFormFocused } = UiContainer.useContainer();
		const [index, setIndex] = useState(-1);
		const t = useTranslation().search;

		const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === 'Enter') {
				const item = suggestions[index];
				if (item) {
					onPickSuggestion(item);
				} else if (onKeyDown) {
					onKeyDown(event);
				}
				setIsSearchFormFocused(false);
				setIndex(-1);
			}
			if (suggestions.length === 0) return;
			switch (event.key) {
				case 'ArrowUp':
					setIndex((index > 0 ? index : suggestions.length) - 1);
					break;
				case 'ArrowDown':
					setIndex((index + 1) % suggestions.length);
					break;
			}
		};

		const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
			const unfocus = event.relatedTarget?.className !== 'search-suggestion';
			event.preventDefault();
			if (unfocus) {
				setIsSearchFormFocused(false);
				setIndex(-1);
			}
		};

		const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
			setIsSearchFormFocused(true);
			if (onFocus) onFocus(e);
		};

		const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
			// Keep the suggestion popup open while the user is actively typing.
			setIsSearchFormFocused(true);
			if (onChange) onChange(e);
		};

		let withinCategoryLabelRendered = false;
		let otherSuggestionsRendered = false;

		return (
			<>
				<input
					onBlur={(e) => handleBlur(e)}
					onFocus={handleFocus}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					value={value}
					ref={ref}
					{...props}
				/>
				{suggestions && (
					<div id="autocompletion" className={isSearchFormFocused ? 'focused' : ''}>
						<ul>
							{suggestions.map((item, i) => {
								const { facetCategory } = item as unknown as AutocompleteItem;
								let sectionDivider = null;

								if (!withinCategoryLabelRendered && facetCategory) {
									sectionDivider = <li className="section">{t.labelWithinCategory}</li>;
									withinCategoryLabelRendered = true;
								} else if (!otherSuggestionsRendered && !facetCategory) {
									sectionDivider = <li className="section">{t.labelOtherSuggestions}</li>;
									otherSuggestionsRendered = true;
								}

								return (
									<div key={i + '_' + JSON.stringify(item)}>
										{sectionDivider}
										<li
											className={index === i ? 'selected' : ''}
											onClick={() => onPickSuggestion(item)}
											onMouseDown={() => onPickSuggestion(item)}
											onFocus={() => setIndex(i)}
											onMouseOver={() => setIndex(i)}
										>
											<Suggestion item={item} />
										</li>
									</div>
								);
							})}
						</ul>
					</div>
				)}
			</>
		);
	}
);
